import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { promptsApi, type PromptResponse } from '../api/client';
import { PROMPT_WORKFLOWS, findWorkflowByPrompt } from '../utils/promptWorkflows';

function formatDateTimeJst(iso: string) {
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function parseTimeMs(value: string): number | null {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function isActiveStatus(status: string): boolean {
  return status === 'active' || status === '有効';
}

/** ADM-S01 プロンプト管理（API 連携） */
export default function AdminPrompts() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [list, setList] = useState<PromptResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    promptsApi
      .list(token)
      .then((data) => {
        setList(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dashboard.errorLoad'));
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [getToken, t]);

  useEffect(() => {
    load();
  }, [load]);

  const nowMs = Date.now();
  const currentByTitle = new Map<string, { id: string; effectiveMs: number }>();
  list.forEach((p) => {
    if (!isActiveStatus(p.status)) return;
    const effectiveMs = parseTimeMs(p.effective_from);
    if (effectiveMs == null || effectiveMs > nowMs) return;
    const prev = currentByTitle.get(p.title);
    if (!prev || effectiveMs > prev.effectiveMs) {
      currentByTitle.set(p.title, { id: p.id, effectiveMs });
    }
  });
  const currentPromptIds = new Set(Array.from(currentByTitle.values()).map((v) => v.id));

  // ワークフロー別の割当状況サマリ（未割当の業務を可視化するため）
  const workflowAssignment = PROMPT_WORKFLOWS.map((wf) => {
    const matched = list.filter((p) => findWorkflowByPrompt(p)?.key === wf.key);
    const current = matched.find((p) => currentPromptIds.has(p.id));
    return { workflow: wf, matchedCount: matched.length, current };
  });
  const unassignedCount = list.filter((p) => findWorkflowByPrompt(p) == null).length;

  return (
    <div className="page admin-prompts-page">
      <h1>{t('admin.prompts')}</h1>
      <p className="admin-prompts-hint">{t('admin.promptsSelectionHint')}</p>

      <div className="admin-card admin-actions">
        <Link to="/admin/prompts/new" className="admin-btn-primary">
          {t('admin.promptsNew')}
        </Link>
      </div>

      <div className="admin-card admin-prompt-workflow-summary">
        <h3>{t('admin.promptWorkflow.summaryTitle')}</h3>
        <p className="admin-prompts-hint">{t('admin.promptWorkflow.summaryIntro')}</p>
        <ul className="admin-prompt-workflow-summary-list">
          {workflowAssignment.map(({ workflow, matchedCount, current }) => (
            <li key={workflow.key}>
              <span className="admin-prompt-workflow-summary-label">{workflow.label}</span>
              <span className="admin-prompt-workflow-summary-desc">{workflow.description}</span>
              <span className="admin-prompt-workflow-summary-status">
                {current ? (
                  <>
                    <span className="admin-badge admin-badge-ok">
                      {t('admin.promptWorkflow.currentLabel', { title: current.title, version: current.version })}
                    </span>
                  </>
                ) : matchedCount > 0 ? (
                  <span className="admin-badge admin-badge-warn">
                    {t('admin.promptWorkflow.draftOnly', { count: matchedCount })}
                  </span>
                ) : (
                  <span className="admin-badge admin-badge-danger">
                    {t('admin.promptWorkflow.unassigned')}
                  </span>
                )}
              </span>
            </li>
          ))}
          {unassignedCount > 0 && (
            <li>
              <span className="admin-prompt-workflow-summary-label">{t('admin.promptWorkflow.otherLabel')}</span>
              <span className="admin-prompt-workflow-summary-desc">{t('admin.promptWorkflow.otherDesc')}</span>
              <span className="admin-prompt-workflow-summary-status">
                <span className="admin-badge">{t('admin.promptWorkflow.otherCount', { count: unassignedCount })}</span>
              </span>
            </li>
          )}
        </ul>
      </div>

      {error && (
        <div className="admin-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={load}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {loading && (
        <div className="admin-card">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="admin-card">
          {list.length === 0 ? (
            <p className="admin-empty">{t('admin.promptsEmpty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.promptsTitle')}</th>
                  <th scope="col">{t('admin.promptWorkflow.columnLabel')}</th>
                  <th scope="col">{t('admin.promptsVersion')}</th>
                  <th scope="col">{t('admin.status')}</th>
                  <th scope="col">{t('admin.promptsEffectiveFrom')}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => {
                  const wf = findWorkflowByPrompt(p);
                  return (
                    <tr key={p.id}>
                      <td>{p.title}</td>
                      <td>
                        {wf ? (
                          <span className="admin-prompt-workflow-cell" title={wf.description}>
                            {wf.label}
                          </span>
                        ) : (
                          <span className="admin-prompt-workflow-cell admin-prompt-workflow-cell-empty">
                            {t('admin.promptWorkflow.unknown')}
                          </span>
                        )}
                      </td>
                      <td>{p.version}</td>
                      <td>
                        <span className={`admin-badge ${p.status === 'active' || p.status === '有効' ? 'admin-badge-ok' : ''}`}>{p.status}</span>
                        {currentPromptIds.has(p.id) && (
                          <span className="admin-badge admin-badge-current">{t('admin.promptsStatusCurrent')}</span>
                        )}
                        {(() => {
                          const effectiveMs = parseTimeMs(p.effective_from);
                          if (effectiveMs == null || effectiveMs <= nowMs) return null;
                          return <span className="admin-badge admin-badge-warn">{t('admin.promptsStatusScheduled')}</span>;
                        })()}
                      </td>
                      <td>{formatDateTimeJst(p.effective_from)} JST</td>
                      <td>
                        <Link to={`/admin/prompts/${p.id}`}>{t('admin.promptsEdit')}</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin">{t('common.back')} → {t('admin.backToDashboard')}</Link>
      </p>
    </div>
  );
}
